import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import api, { getAccessToken } from "@/lib/api";
import manifest from "./i18n/manifest.json";
import en from "./i18n/locales/en.json";
import es from "./i18n/locales/es.json";
import pt from "./i18n/locales/pt.json";

export const dictionaries = { en, es, pt };
export const i18nCatalog = {
  ...manifest,
  messages: dictionaries,
};

export type Language = keyof typeof dictionaries;
export type TranslationKey = keyof typeof dictionaries.en;
export type TranslationParams = Record<string, string | number>;
export type LanguageOption = {
  code: Language;
  flag: string;
  nameKey: TranslationKey;
};
type Dictionary = Record<TranslationKey, string>;

export const languageOptions = manifest.locales
  .filter((item) => item.code in dictionaries)
  .map((item) => ({
    code: item.code as Language,
    flag: item.flag,
    nameKey: item.nameKey as TranslationKey,
  }));

// Numbers and dates follow the country each language is offered for (the
// manifest's flags): a bare "pt" would format as in Brazil.
const NUMBER_LOCALES: Record<Language, string> = {
  es: "es-ES",
  en: "en-US",
  pt: "pt-PT",
};

/** The locale to format numbers in for ``language``. */
export function numberLocale(language: Language): string {
  return NUMBER_LOCALES[language];
}

const fallbackLanguage = manifest.fallbackLanguage as Language;
const defaultLanguage = manifest.defaultLanguage as Language;
const supportedLanguages = new Set<Language>(
  Object.keys(dictionaries) as Language[],
);

const languageStorageKey = "sentinels_language";
const languagePreferenceStorageKey = "sentinels_language_preference_set";

interface I18nContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  confirmLanguagePreference: (language?: Language) => Promise<void>;
  syncLanguagePreference: (
    locale: string | null | undefined,
    confirmed: boolean | null | undefined,
  ) => void;
  hasLanguagePreference: boolean;
  t: (key: TranslationKey, params?: TranslationParams) => string;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

function normalizeLanguage(value: string | null | undefined): Language {
  const base = value?.toLowerCase().split("-")[0] as Language | undefined;
  if (base && supportedLanguages.has(base)) return base;
  return defaultLanguage;
}

/** The language the user chose on this browser, or the default. */
function storedLanguage(): Language {
  if (typeof window === "undefined") return defaultLanguage;
  try {
    const hasUserPreference =
      localStorage.getItem(languagePreferenceStorageKey) === "true";
    if (!hasUserPreference) return defaultLanguage;
    return normalizeLanguage(localStorage.getItem(languageStorageKey));
  } catch {
    return defaultLanguage;
  }
}

// The language on screen, for formatting code outside React components
// (plain helpers cannot read the context). The provider keeps it current.
let activeLanguage: Language = storedLanguage();

function rememberLanguage(language: Language): Language {
  activeLanguage = language;
  return language;
}

/**
 * The locale to format numbers and dates in for the language on screen:
 * es-ES, en-US or pt-PT. Use it instead of a hard-coded locale, e.g.
 * ``value.toLocaleString(activeLocale())``.
 */
export function activeLocale(): string {
  return numberLocale(activeLanguage);
}

/**
 * Translate ``key`` into the language on screen, for plain helpers and
 * queues that run outside React components (they cannot read the context).
 * Components keep using ``useI18n().t``.
 */
export function translateActive(
  key: TranslationKey,
  params?: TranslationParams,
): string {
  return translate(activeLanguage, key, params);
}

async function saveLanguagePreference(language: Language): Promise<void> {
  if (typeof window === "undefined") return;
  // Ask the API client whether there is a session: under OIDC the access
  // token lives in memory only, never in localStorage, so reading storage
  // here skipped the save and a reload went back to the old language (F-09).
  if (!getAccessToken()) return;
  await api.patch("/accounts/session/preferences/", { locale: language });
}

function interpolate(
  message: string,
  params: TranslationParams | undefined,
): string {
  if (!params) return message;
  return message.replace(/\{(\w+)\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(params, key)
      ? String(params[key])
      : match,
  );
}

function translate(
  language: Language,
  key: TranslationKey,
  params?: TranslationParams,
): string {
  const dictionary = dictionaries[language] as Dictionary;
  const fallback = dictionaries[fallbackLanguage] as Dictionary;
  return interpolate(dictionary[key] ?? fallback[key], params);
}

const fallbackI18nContext: I18nContextValue = {
  language: fallbackLanguage,
  setLanguage: () => undefined,
  confirmLanguagePreference: async () => undefined,
  syncLanguagePreference: () => undefined,
  hasLanguagePreference: false,
  t: (key, params) => translate(fallbackLanguage, key, params),
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [hasLanguagePreference, setHasLanguagePreference] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(languagePreferenceStorageKey) === "true";
  });
  const [language, setLanguageState] = useState<Language>(() =>
    rememberLanguage(storedLanguage()),
  );

  useEffect(() => {
    document.documentElement.lang = language;
    localStorage.setItem(languageStorageKey, language);
  }, [language]);

  const markLanguagePreference = useCallback((nextLanguage: Language) => {
    localStorage.setItem(languagePreferenceStorageKey, "true");
    localStorage.setItem(languageStorageKey, nextLanguage);
    setHasLanguagePreference(true);
    setLanguageState(rememberLanguage(nextLanguage));
  }, []);

  const confirmLanguagePreference = useCallback(
    async (nextLanguage: Language = language) => {
      markLanguagePreference(nextLanguage);
      await saveLanguagePreference(nextLanguage);
    },
    [language, markLanguagePreference],
  );

  const setLanguage = useCallback(
    (nextLanguage: Language) => {
      markLanguagePreference(nextLanguage);
      void saveLanguagePreference(nextLanguage).catch(() => {
        // Keep the local preference even if the account sync is temporarily unavailable.
      });
    },
    [markLanguagePreference],
  );

  const syncLanguagePreference = useCallback(
    (
      locale: string | null | undefined,
      confirmed: boolean | null | undefined,
    ) => {
      const nextLanguage = normalizeLanguage(locale);
      const localPreference =
        localStorage.getItem(languagePreferenceStorageKey) === "true";

      if (confirmed) {
        markLanguagePreference(nextLanguage);
        return;
      }

      if (localPreference) {
        const localLanguage = normalizeLanguage(
          localStorage.getItem(languageStorageKey),
        );
        markLanguagePreference(localLanguage);
        void saveLanguagePreference(localLanguage).catch(() => {
          // The prompt can stay dismissed locally while the next session retries.
        });
        return;
      }

      localStorage.removeItem(languagePreferenceStorageKey);
      setHasLanguagePreference(false);
      setLanguageState(rememberLanguage(defaultLanguage));
    },
    [markLanguagePreference],
  );

  const t = useCallback(
    (key: TranslationKey, params?: TranslationParams) =>
      translate(language, key, params),
    [language],
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      confirmLanguagePreference,
      syncLanguagePreference,
      hasLanguagePreference,
      t,
    }),
    [
      language,
      setLanguage,
      confirmLanguagePreference,
      syncLanguagePreference,
      hasLanguagePreference,
      t,
    ],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  return context ?? fallbackI18nContext;
}
